"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Calendar, Phone, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const trustPoints = [{
  title: "Transparent milestone estimates",
  subtitle: "Fixed pricing with clear allowances"
}, {
  title: "Dedicated post-remodel support",
  subtitle: "Attentive follow-up and continued care"
}];
export default function Action() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="call-to-action" data-nav="light" className="relative bg-background text-foreground py-20 md:py-24 overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8 max-w-6xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.25
      }} className="relative rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 md:p-16 border border-border/20 shadow-2xl overflow-hidden">
          {/* Subtle architectural hairline divider motif */}
          <div aria-hidden="true" className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

          <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center">
            {/* Local craft badge */}
            <Badge variant="outline" className="mb-6 rounded-full px-4 py-1 border-primary/40 bg-primary/10 text-primary-foreground font-semibold text-xs tracking-widest uppercase">
              Brookhaven Residential Remodeling
            </Badge>

            {/* Editorial Serif Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight text-secondary-foreground">
              Design your private bathroom retreat.
            </h2>

            {/* Architectural Gold Hairline */}
            <div aria-hidden="true" className="w-16 h-0.5 bg-primary/60 my-6 rounded-full" />

            {/* Plain-spoken supporting copy */}
            <p className="text-base sm:text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed mb-8">
              Plan your Brookhaven remodel with transparent milestone pricing, tailored cabinetry, and dedicated daily site leadership.
            </p>

            {/* Primary & Secondary Call to Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto mb-6">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold px-8 py-3.5 rounded-lg shadow-md hover:bg-primary/90 transition-[background-color,box-shadow]">
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request an estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-secondary-foreground/20 text-secondary-foreground bg-transparent hover:bg-secondary-foreground/10 hover:text-secondary-foreground px-7 py-3.5 rounded-lg transition-[background-color,border-color]">
                <Link to="/AboutPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
                  <span>Explore our process</span>
                </Link>
              </Button>
            </div>

            {/* Secondary guide link */}
            <div className="mb-10">
              <Link to="#" className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary-foreground/80 hover:text-primary underline underline-offset-4 transition-colors">
                <span>Read our full bathroom remodeling guide</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </div>

            {/* Direct telephone option */}
            <div className="inline-flex items-center gap-2 text-sm text-secondary-foreground/75 mb-10">
              <span>Prefer to talk directly?</span>
              <a href="tel:+14708479076" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline transition-colors">
                <Phone className="size-3.5" />
                <span>(470) 847-9076</span>
              </a>
            </div>

            {/* Hairline internal rule */}
            <div aria-hidden="true" className="w-full h-px bg-secondary-foreground/15 mb-10" />

            {/* Trust credential highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full text-left">
              {trustPoints.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-3.5 p-4 rounded-xl bg-secondary-foreground/5 border border-secondary-foreground/10">
                  <div className="shrink-0 mt-0.5 rounded-full p-1 bg-primary/15 text-primary">
                    <CheckCircle2 className="size-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-secondary-foreground leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-xs text-secondary-foreground/70 mt-0.5">
                      {item.subtitle}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}